import { baseApi } from './baseApi';
import type { ApiResponse, ManualService, PaginationMeta } from '../../types/api';

interface GetManualServicesParams {
  visible?: boolean;
  maintenance?: boolean;
  page?: number;
  limit?: number;
}

interface CreateManualServiceRequest {
  key: string;
  title: string;
  titleEn?: string;
  description?: string;
  descriptionEn?: string;
  visible?: boolean;
  maintenance?: boolean;
  order?: number;
}

interface UpdateManualServiceRequest {
  title?: string;
  titleEn?: string;
  description?: string;
  descriptionEn?: string;
  visible?: boolean;
  maintenance?: boolean;
  order?: number;
}

export const manualServicesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getManualServices: builder.query<
      ApiResponse<{ services: ManualService[]; pagination: PaginationMeta }>,
      GetManualServicesParams | undefined
    >({
      query: (params) => ({
        url: '/manual-services',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: ['ManualService'],
    }),
    getManualServiceByKey: builder.query<ApiResponse<{ service: ManualService }>, string>({
      query: (key) => ({
        url: `/manual-services/${key}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, key) => [{ type: 'ManualService', id: key }],
    }),
    createManualService: builder.mutation<ApiResponse<{ service: ManualService; message: string }>, CreateManualServiceRequest>({
      query: (data) => ({
        url: '/manual-services',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ManualService'],
    }),
    updateManualService: builder.mutation<
      ApiResponse<{ service: ManualService; message: string }>,
      { key: string; data: UpdateManualServiceRequest }
    >({
      query: ({ key, data }) => ({
        url: `/manual-services/${key}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'ManualService', id: key }, 'ManualService'],
    }),
    patchManualService: builder.mutation<
      ApiResponse<{ service: ManualService; message: string }>,
      { key: string; data: Partial<UpdateManualServiceRequest> }
    >({
      query: ({ key, data }) => ({
        url: `/manual-services/${key}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'ManualService', id: key }, 'ManualService'],
    }),
    deleteManualService: builder.mutation<ApiResponse<{ message: string }>, string>({
      query: (key) => ({
        url: `/manual-services/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ManualService'],
    }),
  }),
});

export const {
  useGetManualServicesQuery,
  useGetManualServiceByKeyQuery,
  useCreateManualServiceMutation,
  useUpdateManualServiceMutation,
  usePatchManualServiceMutation,
  useDeleteManualServiceMutation,
} = manualServicesApi;

